import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import UsageWidget from "../components/UsageWidget";
import UpgradeNudge from "../components/UpgradeNudge";
import { useLanguage } from "../contexts/LanguageContext";
import OnboardingTour from "../components/OnboardingTour";
import {
  Zap,
  Briefcase,
  FileText,
  MessageSquare,
  TrendingUp,
  Users,
  LogOut,
  Settings,
  ChevronRight,
  MapPin,
  Clock,
  Building2,
  Sparkles,
  User,
  Award,
  Flame,
  BarChart3,
  Search,
  Calendar,
  Target,
  Video,
  Globe
} from "lucide-react";

const JobSeekerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const [dashboardData, setDashboardData] = useState(null);
  const [matchingJobs, setMatchingJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [topIndustries, setTopIndustries] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchUnreadCount();
    fetchMatchingJobs();
    fetchTopIndustries();
  }, []);

  const fetchTopIndustries = async () => {
    try {
      const res = await fetch(`${API}/jobs/browse/industries`);
      if (res.ok) setTopIndustries((await res.json()).slice(0, 6));
    } catch {}
  };

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API}/dashboard/jobseeker`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMatchingJobs = async () => {
    try {
      const response = await fetch(`${API}/matching/jobs?limit=10`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        // Filter to only show jobs with a match score >= 50%
        const filteredJobs = data.filter(item => item.match_score >= 50);
        setMatchingJobs(filteredJobs);
      }
    } catch (error) {
      console.error("Failed to fetch matching jobs:", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API}/messages/unread/count`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleApply = async (jobId) => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}/apply`, {
        method: "POST",
        credentials: "include",
      });
      if (response.ok) {
        toast.success("Application submitted!");
        fetchDashboard();
        fetchMatchingJobs();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.detail || "Failed to apply");
      }
    } catch (error) {
      toast.error("Failed to apply");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Onboarding Tour */}
      <OnboardingTour userRole="jobseeker" />
      
      {/* Sidebar */}
      <Sidebar unreadCount={unreadCount} />

      {/* Main Content */}
      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8" data-testid="jobseeker-dashboard">
        {/* Upgrade Nudge */}
        <UpgradeNudge />

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1">
              {t("welcomeBack")}, <span className="text-crimson">{user?.name?.split(' ')[0]}</span>
            </h1>
            <p className="text-gray-400 text-sm sm:text-base">{t("careerJourney")}</p>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/profile/build")}
              className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider text-sm"
              data-testid="edit-profile-btn"
            >
              {t("editProfile")}
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8">
          <motion.div 
            className="stat-card cursor-pointer hover:border-crimson/30 transition-colors"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            onClick={() => navigate("/my-applications?tab=applications")}
            data-testid="applications-stat"
          >
            <div className="stat-value">{dashboardData?.stats?.applications || 0}</div>
            <div className="stat-label">{t("applications")}</div>
          </motion.div>
          <motion.div 
            className="stat-card cursor-pointer hover:border-crimson/30 transition-colors"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => navigate("/my-applications?tab=interviews")}
            data-testid="interviews-stat"
          >
            <div className="stat-value">{dashboardData?.stats?.interviews || 0}</div>
            <div className="stat-label">{t("interviews")}</div>
          </motion.div>
          <motion.div 
            className="stat-card cursor-pointer hover:border-crimson/30 transition-colors"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => navigate("/profile/build")}
            data-testid="profile-stat"
          >
            <div className="stat-value">{dashboardData?.stats?.profile_complete ? "100%" : "50%"}</div>
            <div className="stat-label">Profile Complete</div>
          </motion.div>
        </div>

        {/* Profile Alert */}
        {!dashboardData?.stats?.profile_complete && (
          <motion.div 
            className="bg-crimson/10 border border-crimson/30 p-4 sm:p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
              </div>
              <div>
                <h3 className="font-heading text-base sm:text-lg font-bold">Complete Your Profile</h3>
                <p className="text-gray-400 text-xs sm:text-sm">Add your skills and experience to get better job matches</p>
              </div>
            </div>
            <Button
              onClick={() => navigate("/profile/build")}
              className="bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider text-xs sm:text-sm w-full sm:w-auto"
              data-testid="complete-profile-btn"
            >
              Complete Now
            </Button>
          </motion.div>
        )}

        {/* Usage Widget for Job Seekers */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2">
            {/* Career Progress Summary */}
            <motion.div
              className="bg-obsidian-light border border-[#222] p-6 h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <h3 className="font-heading text-lg font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-crimson" />
                Career Progress
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div 
                  className="text-center p-4 bg-obsidian border border-[#333] cursor-pointer hover:border-crimson/30 transition-colors"
                  onClick={() => navigate("/my-applications?tab=applications")}
                >
                  <div className="text-2xl font-bold text-crimson">{dashboardData?.stats?.applications || 0}</div>
                  <div className="text-xs text-gray-500 uppercase">Applied</div>
                </div>
                <div 
                  className="text-center p-4 bg-obsidian border border-[#333] cursor-pointer hover:border-blue-500/30 transition-colors"
                  onClick={() => navigate("/my-applications?tab=interviews")}
                >
                  <div className="text-2xl font-bold text-blue-400">{dashboardData?.stats?.interviews || 0}</div>
                  <div className="text-xs text-gray-500 uppercase">Interviews</div>
                </div>
                <div 
                  className="text-center p-4 bg-obsidian border border-[#333] cursor-pointer hover:border-green-500/30 transition-colors"
                  onClick={() => navigate("/my-applications?tab=offers")}
                >
                  <div className="text-2xl font-bold text-green-400">{dashboardData?.stats?.offers || 0}</div>
                  <div className="text-xs text-gray-500 uppercase">Offers</div>
                </div>
                <div 
                  className="text-center p-4 bg-obsidian border border-[#333] cursor-pointer hover:border-purple-500/30 transition-colors"
                  onClick={() => navigate("/jobs")}
                >
                  <div className="text-2xl font-bold text-purple-400">{matchingJobs.length}</div>
                  <div className="text-xs text-gray-500 uppercase">Matches</div>
                </div>
              </div>
            </motion.div>
          </div>
          <div>
            <UsageWidget />
          </div>
        </div>

        {/* Quick Browse by Industry */}
        {topIndustries.length > 0 && (
          <div className="mb-8" data-testid="quick-browse">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="font-heading text-lg sm:text-2xl font-bold">QUICK BROWSE</h2>
                <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
              </div>
              <Link to="/jobs" className="text-crimson text-xs sm:text-sm uppercase tracking-widest hover:underline">
                Browse All
              </Link>
            </div>
            <div className="flex flex-wrap gap-2">
              {topIndustries.map((ind) => (
                <Link
                  key={ind.industry}
                  to={`/jobs?industry=${encodeURIComponent(ind.industry)}`}
                  className="px-4 py-2.5 bg-obsidian-light border border-[#222] hover:border-crimson/50 text-sm text-gray-300 hover:text-white transition-all"
                  data-testid={`quick-${ind.industry}`}
                >
                  {ind.industry}
                  <span className="ml-2 text-crimson text-xs font-bold">{ind.count}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Matching Jobs */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-3">
              <h2 className="font-heading text-lg sm:text-2xl font-bold">MATCHING JOBS</h2>
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-crimson" />
            </div>
            <Link to="/jobs" className="text-crimson text-xs sm:text-sm uppercase tracking-widest hover:underline" data-testid="view-all-jobs">
              View All
            </Link>
          </div>

          {matchingJobs.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {matchingJobs.slice(0, 6).map((item, index) => {
                const job = item.job;
                const matchScore = item.match_score;
                const matchDetails = item.match_details;
                
                // Determine match level color
                const getMatchColor = (score) => {
                  if (score >= 80) return "text-green-400 bg-green-500/20 border-green-500/30";
                  if (score >= 60) return "text-blue-400 bg-blue-500/20 border-blue-500/30";
                  if (score >= 40) return "text-yellow-400 bg-yellow-500/20 border-yellow-500/30";
                  return "text-orange-400 bg-orange-500/20 border-orange-500/30";
                };
                
                return (
                  <motion.div
                    key={job.job_id}
                    className={`bg-obsidian-light border p-4 sm:p-6 hover:border-crimson/50 transition-all card-hover relative ${
                      job.promoted ? "border-crimson/50" : "border-[#222]"
                    }`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`matching-job-${job.job_id}`}
                  >
                    {/* Match Score Badge */}
                    <div className={`absolute -top-2 sm:-top-3 -right-2 sm:-right-3 px-2 sm:px-3 py-1 sm:py-1.5 border font-bold text-xs sm:text-sm ${getMatchColor(matchScore)}`}>
                      {matchScore}% Match
                    </div>

                    {/* Promoted Badge */}
                    {job.promoted && (
                      <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-crimson px-1.5 sm:px-2 py-0.5 sm:py-1 flex items-center gap-1">
                        <Flame className="w-2 h-2 sm:w-3 sm:h-3 text-white" />
                        <span className="text-[10px] sm:text-xs uppercase tracking-wider text-white font-bold">Promoted</span>
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-3 sm:mb-4 mt-1 sm:mt-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                          <h3 className="font-heading text-base sm:text-lg font-bold break-words">{job.title}</h3>
                          {/* Featured Employer Badge */}
                          {job.featured_employer && (
                            <span className="inline-flex items-center gap-1 px-1.5 sm:px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/50">
                              <Award className="w-2 h-2 sm:w-3 sm:h-3 text-yellow-500" />
                              <span className="text-[10px] sm:text-xs text-yellow-500">Verified</span>
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-xs sm:text-sm">{job.industry}</p>
                        {job.recruiter_name && (
                          <p className="text-gray-500 text-[10px] sm:text-xs mt-1">by {job.recruiter_name}</p>
                        )}
                      </div>
                      <span className="tag text-[10px] sm:text-xs mt-2 sm:mt-0">{job.employment_type}</span>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">
                      {job.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span className="truncate">{job.location}</span>
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        {job.seniority_level}
                      </span>
                    </div>

                    {/* Match Details */}
                    {matchDetails && (
                      <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-obsidian border border-[#1a1a1a]">
                        <div className="flex items-center justify-between mb-1 sm:mb-2">
                          <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wider">Match Breakdown:</p>
                          {item.match_grade && (
                            <span className={`text-xs font-bold px-2 py-0.5 ${
                              item.match_grade.startsWith('A') ? 'bg-green-500/20 text-green-400' :
                              item.match_grade.startsWith('B') ? 'bg-blue-500/20 text-blue-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              Grade {item.match_grade}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-1 sm:gap-2 text-[10px] sm:text-xs">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Skills:</span>
                            <span className={matchDetails.skills?.score >= 50 ? "text-green-400" : "text-yellow-400"}>
                              {matchDetails.skills?.score || 0}%
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Experience:</span>
                            <span className={matchDetails.experience?.score >= 75 ? "text-green-400" : "text-yellow-400"}>
                              {matchDetails.experience?.score || 0}%
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Location:</span>
                            <span className={matchDetails.location?.score >= 80 ? "text-green-400" : "text-yellow-400"}>
                              {matchDetails.location?.score || 0}%
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500">Salary:</span>
                            <span className={matchDetails.salary?.score >= 70 ? "text-green-400" : "text-yellow-400"}>
                              {matchDetails.salary?.score || 0}%
                            </span>
                          </div>
                        </div>
                        {matchDetails.skills?.exact_matches?.length > 0 && (
                          <div className="mt-1 sm:mt-2">
                            <p className="text-gray-500 text-[10px] sm:text-xs truncate">
                              Matching skills: {matchDetails.skills.exact_matches.slice(0, 3).join(", ")}
                            </p>
                          </div>
                        )}
                        {matchDetails.skills?.missing_critical?.length > 0 && (
                          <div className="mt-1">
                            <p className="text-yellow-500/80 text-[10px] sm:text-xs truncate">
                              Consider learning: {matchDetails.skills.missing_critical.slice(0, 2).join(", ")}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {job.skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 sm:gap-2 mb-3 sm:mb-4">
                        {job.skills.slice(0, 3).map((skill, idx) => (
                          <span 
                            key={idx} 
                            className={`px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs ${
                              matchDetails?.skills?.exact_matches?.map(s => s.toLowerCase()).includes(skill.toLowerCase())
                                ? "bg-green-500/20 text-green-400" 
                                : "bg-[#1a1a1a] text-gray-300"
                            }`}
                          >
                            {skill}
                          </span>
                        ))}
                        {job.skills.length > 3 && (
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs text-gray-500">+{job.skills.length - 3}</span>
                        )}
                      </div>
                    )}

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                      <Button
                        onClick={() => navigate(`/jobs/${job.job_id}`)}
                        variant="outline"
                        className="flex-1 border-[#333] hover:border-crimson text-white uppercase tracking-wider text-xs sm:text-sm py-2 sm:py-auto"
                        data-testid={`view-job-${job.job_id}`}
                      >
                        View Details
                      </Button>
                      <Button
                        onClick={() => handleApply(job.job_id)}
                        className="flex-1 bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider text-xs sm:text-sm py-2 sm:py-auto"
                        data-testid={`apply-job-${job.job_id}`}
                      >
                        Quick Apply
                      </Button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="bg-obsidian-light border border-[#222] p-12 text-center">
              <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">No Matching Jobs Yet</h3>
              <p className="text-gray-400 mb-6">Complete your profile to get personalized job recommendations</p>
              <Button
                onClick={() => navigate("/profile/build")}
                className="bg-crimson hover:bg-crimson-hover text-white uppercase tracking-wider"
              >
                Build Profile
              </Button>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <button
            onClick={() => navigate("/career-insights")}
            className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 bg-obsidian-light border border-[#222] hover:border-crimson/50 transition-all text-left group"
            data-testid="quick-action-insights"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/10 flex items-center justify-center group-hover:bg-crimson/20 transition-colors flex-shrink-0">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-base sm:text-lg font-bold">Career Insights</h3>
              <p className="text-gray-400 text-xs sm:text-sm truncate">Get AI-powered recommendations</p>
            </div>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 group-hover:text-crimson transition-colors flex-shrink-0" />
          </button>

          <button
            onClick={() => navigate("/community")}
            className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 bg-obsidian-light border border-[#222] hover:border-crimson/50 transition-all text-left group"
            data-testid="quick-action-community"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/10 flex items-center justify-center group-hover:bg-crimson/20 transition-colors flex-shrink-0">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-base sm:text-lg font-bold">Community</h3>
              <p className="text-gray-400 text-xs sm:text-sm truncate">Connect with professionals</p>
            </div>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 group-hover:text-crimson transition-colors flex-shrink-0" />
          </button>

          <button
            onClick={() => navigate("/subscription")}
            className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 bg-obsidian-light border border-[#222] hover:border-crimson/50 transition-all text-left group sm:col-span-2 lg:col-span-1"
            data-testid="quick-action-upgrade"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-crimson/10 flex items-center justify-center group-hover:bg-crimson/20 transition-colors flex-shrink-0">
              <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-crimson" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-base sm:text-lg font-bold">Upgrade</h3>
              <p className="text-gray-400 text-xs sm:text-sm truncate">Unlock premium features</p>
            </div>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 group-hover:text-crimson transition-colors flex-shrink-0" />
          </button>
        </div>
      </main>
    </div>
  );
};

export default JobSeekerDashboard;
